import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, AlertTriangle, ChevronDown, Mail, Calendar } from '../icons';
import type { PartnerOverview } from '../../lib/admin-dashboard';

interface PartnerOverviewTableProps {
  partners: PartnerOverview[];
}

function formatDate(dateStr: string): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function PartnerOverviewTable({ partners }: PartnerOverviewTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (partners.length === 0) {
    return (
      <div className="freud-card-elevated p-6">
        <h3 className="text-text-xl text-gray-80 mb-4">Partner Overview</h3>
        <p className="text-text-sm text-gray-40 text-center py-6">
          No partners registered yet
        </p>
      </div>
    );
  }

  const toggleExpand = (id: string) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  return (
    <div className="freud-card-elevated p-6">
      <div className="flex items-center gap-2 mb-5">
        <Building2 className="w-5 h-5 text-brand-40" />
        <h3 className="text-text-xl text-gray-80">Partner Overview</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-text-sm">
          <thead>
            <tr className="border-b border-gray-20">
              <th className="text-left py-3 px-3 text-gray-50 font-semibold">Partner</th>
              <th className="text-center py-3 px-3 text-gray-50 font-semibold">Coaches</th>
              <th className="text-center py-3 px-3 text-gray-50 font-semibold">Respondents</th>
              <th className="text-center py-3 px-3 text-gray-50 font-semibold">Assessments</th>
              <th className="text-center py-3 px-3 text-gray-50 font-semibold">Codes</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {partners.map((partner, index) => {
              const nearCapacity = partner.coachCount >= partner.maxCoaches * 0.8;
              const isExpanded = expandedId === partner.partnerId;
              return (
                <motion.tr
                  key={partner.partnerId}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b border-gray-10 hover:bg-brand-5 transition-colors cursor-pointer"
                  onClick={() => toggleExpand(partner.partnerId)}
                >
                  <td className="py-3 px-3" colSpan={6}>
                    <div className="flex items-center">
                      <div className="flex-1 min-w-0">
                        <span className="text-gray-80 font-medium">{partner.partnerName}</span>
                      </div>
                      <div className="flex-1 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <span className={nearCapacity ? 'text-orange-50 font-semibold' : 'text-gray-60'}>
                            {partner.coachCount}
                          </span>
                          <span className="text-gray-40">/ {partner.maxCoaches}</span>
                          {nearCapacity && (
                            <AlertTriangle className="w-3.5 h-3.5 text-orange-40" />
                          )}
                        </div>
                      </div>
                      <div className="flex-1 text-center text-gray-60">{partner.respondentCount}</div>
                      <div className="flex-1 text-center">
                        <span className="text-gray-80 font-semibold">{partner.assessmentCount}</span>
                      </div>
                      <div className="flex-1 text-center">
                        <span className="text-gray-60">{partner.codesUsed}</span>
                        <span className="text-gray-40"> / {partner.codesTotal}</span>
                      </div>
                      <div className="w-10 flex justify-center">
                        <motion.div
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown className="w-4 h-4 text-gray-40" />
                        </motion.div>
                      </div>
                    </div>
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-3 pt-3 border-t border-gray-15 flex flex-wrap gap-6">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-brand-40" />
                              <span className="text-text-sm text-gray-50">Email:</span>
                              <span className="text-text-sm text-gray-80 font-medium">
                                {partner.email || 'N/A'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-brand-40" />
                              <span className="text-text-sm text-gray-50">Date Joined:</span>
                              <span className="text-text-sm text-gray-80 font-medium">
                                {formatDate(partner.dateJoined)}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
